import React, { PropsWithChildren, useCallback, useMemo } from "react";

import { isAxiosError } from "axios";
import { useHistory, useParams } from "react-router-dom";

import { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useGetGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlan";
import usePatchGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/usePatchGoalBasedFinancialPlan";
import { GoalBasedFinancialPlanStatus } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { FinancialPlansPath } from "@src/config/routing/RouterPaths";
import ProgressBar from "@src/multiCustodian/components/FinancialPlans/ProgressBar/ProgressBar";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import BottomBar from "@src/sharedComponents/BottomBar/BottomBar";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";

import FlowSteps from "./FlowSteps";
import { CreateUpdateFlowScrollContainerDivID } from "./pages/Shared/constants";
import useGetClientAndSpouseDetails from "./pages/Shared/hooks/useGetClientAndSpouseDetails";
import { actions } from "./reducer/actions";
import { useCreateUpdateFinancialPlanContext } from "./reducer/Context";
import { PageIndex } from "./reducer/types";
import { getProgressStateFromPageIndex } from "./utils";

import styles from "./Flow.module.css";

const FinancialPlansCreateUpdateFlow: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const history = useHistory();

  const auth = useRequestAuth();

  const notify = useStatusNotification();

  const financialPlan = useGetGoalBasedFinancialPlan(
    goalBasedFinancialPlanId,
    auth
  );

  const {
    mutateAsync: patchGoalBasedFinancialPlan,
    isLoading: isMutatingFincancialPlan,
  } = usePatchGoalBasedFinancialPlan(auth);

  const {
    data: singleGoalBasedFinancialPlan,
    isLoading: isLoadingFinancialPlan,
  } = useGetGoalBasedFinancialPlan(goalBasedFinancialPlanId, auth);

  const {
    state: { pageIndex },
    dispatch,
  } = useCreateUpdateFinancialPlanContext();

  const { isLoading: isLoadingClientAndSpouseData, data: clientSpouseData } =
    useGetClientAndSpouseDetails(clientId);

  const progressState = useMemo(() => {
    return getProgressStateFromPageIndex(pageIndex, financialPlan.data);
  }, [pageIndex, financialPlan.data]);

  const returnToPlansLandingPage = useCallback(() => {
    dispatch({ type: actions.RESET_TO_DEFAULT_DATA, payload: undefined });

    history.push({
      ...history.location,
      pathname: `/Client/${clientId}/${FinancialPlansPath}/Dashboard/${goalBasedFinancialPlanId}`,
    });
  }, [clientId, dispatch, history, goalBasedFinancialPlanId]);

  const handleOnClickBack = () => {
    switch (pageIndex) {
      case 0:
        returnToPlansLandingPage();
        break;
      default:
        dispatch({ type: actions.DECREMENT_PAGE_INDEX, payload: undefined });
        break;
    }
  };

  const handleOnClickNext = () => {
    switch (pageIndex) {
      case 7:
        onFinishFinancialPlan();
        break;
      default:
        dispatch({ type: actions.INCREMENT_PAGE_INDEX, payload: undefined });
        break;
    }
  };

  const handleOnSetPage = (pageNumber: PageIndex) =>
    dispatch({ type: actions.SET_PAGE_INDEX, payload: pageNumber });

  const onFinishFinancialPlan = async () => {
    if (singleGoalBasedFinancialPlan === undefined) {
      throw new Error("Financial plan not found");
    }

    if (
      financialPlan.data?.status === GoalBasedFinancialPlanStatus.Enum.Complete
    ) {
      returnToPlansLandingPage();
      return; // client has already finished the plan and made updates
    }

    const body = {
      status: GoalBasedFinancialPlanStatus.Enum.Complete,
    };

    try {
      await patchGoalBasedFinancialPlan({
        goalBasedFinancialPlanId,
        body,
      });

      notify("Financial plan finished", "Success");

      returnToPlansLandingPage();
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error creating plan ${message ?? ""}`, "Error");
      } else {
        notify("Error finishing financial plan", "Error");
      }
    }
  };

  const showLoadingLogo =
    isLoadingFinancialPlan ||
    isMutatingFincancialPlan ||
    isLoadingClientAndSpouseData;

  const flowStepsDataIsReady =
    singleGoalBasedFinancialPlan !== undefined &&
    clientSpouseData !== undefined;

  const shouldDisableContinueButton =
    pageIndex === 1 &&
    singleGoalBasedFinancialPlan?.retirementHorizon.length === 0;

  return (
    <div className={styles.wizardContainer}>
      <ProgressBar progressState={progressState} setPage={handleOnSetPage} />

      {showLoadingLogo && <LogoLoadingStill onTop noBackgroundColor />}

      {!showLoadingLogo && flowStepsDataIsReady && (
        <div
          className={styles.flowContainer}
          id={CreateUpdateFlowScrollContainerDivID}
        >
          <FlowSteps
            clientSpouseData={clientSpouseData}
            singleGoalBasedFinancialPlan={singleGoalBasedFinancialPlan}
          />
        </div>
      )}

      {children}

      <BottomBar
        start={
          <LinkButton
            text="Exit"
            variant="text"
            buttonType="primary"
            to={`/Client/${clientId}/${FinancialPlansPath}/Dashboard/${goalBasedFinancialPlanId}`}
          />
        }
        end={
          <div className={styles.bottomBarEnd}>
            <Button
              text="Back"
              variant="text"
              buttonType="primary"
              onClick={handleOnClickBack}
            />

            <Button
              text={pageIndex === 7 ? "Finish Plan" : "Continue"}
              variant="contained"
              buttonType="primary"
              disabled={shouldDisableContinueButton}
              onClick={handleOnClickNext}
            />
          </div>
        }
      />
    </div>
  );
};

export default FinancialPlansCreateUpdateFlow;
